import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { StorySegment, ChatMessage, UserProfile, AIAgent, PlayerSession, KarmaImpact, StoryContext } from '@/types';
import { AIAgentService } from '@/services/aiAgentService';
import { KarmaEngine } from '@/services/karmaEngine';
import { gitGameStateService } from '@/services/gitGameStateService';
import { fallbackGitStateService } from '@/services/fallbackGitStateService';
import { karmaService } from '@/services/karmaService';
import { yearZeroEngine } from '@/services/yearZeroEngine';
import { audioTestingService } from '@/services/audioTestingService';
import { supabaseClient } from '@/services/supabaseClient';
import { UnifiedAIService } from '@/services/unifiedAiService';
import { generateStorySegment as generateDynamicStorySegment } from '@/services/openRouterToElevenLabsAdapter';
import LoadingIndicator from './LoadingIndicator';
import InteractiveGitTreeVisualizer from './InteractiveGitTreeVisualizer';
import { v4 as uuidv4, validate as uuidValidate } from 'uuid';
import MusicPlayer from './MusicPlayer';
import { elevenLabsService } from '@/services/elevenlabsService';
import { questService, Quest } from '@/services/questService';
import QuestPanel from './QuestPanel';
import { autonomousEcosystemService } from '@/services/autonomousEcosystemService';
import { levelUpService } from '@/services/levelUpService';
import { soundscapeService } from '@/services/soundscapeService';
import { safeTTSService } from '@/services/safeTTSService';
import { sessionPersistenceService } from '@/services/sessionPersistenceService';
import { locationTrackingService, type PlayerLocation } from '@/services/locationTrackingService';
import { dynamicCompanionService, type DynamicCompanion } from '@/services/dynamicCompanionService';
import { githubWorldStateService } from '@/services/githubWorldStateService';
import { mutationService, MUTATIONS, type Mutation } from '@/services/mutationService';
import GraphicalMode from './GraphicalMode';
import { ErrorBoundary } from './ErrorBoundary';

// Cyberpunk Image Loader Component
interface CyberpunkImageLoaderProps {
  src?: string;
  alt: string;
  className?: string;
  loadingText?: string;
  enableGlitch?: boolean;
  onRetry?: () => void;
}

const CyberpunkImageLoader: React.FC<CyberpunkImageLoaderProps> = ({
  src,
  alt,
  className = '',
  loadingText = 'Loading...',
  enableGlitch = false,
  onRetry
}) => {
  const [imageState, setImageState] = useState<'loading' | 'loaded' | 'error'>('loading');
  const [imageSrc, setImageSrc] = useState<string | undefined>(src);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    if (src) {
      setImageState('loading');
      setImageSrc(src);
      setRetryCount(0);
    }
  }, [src]);

  const handleImageLoad = () => {
    setImageState('loaded');
  };

  const handleImageError = () => {
    console.warn('Failed to load scene image:', imageSrc);
    setImageState('error');
  };

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    } else {
      setRetryCount(prev => prev + 1);
      setImageState('loading');
      // Force reload by adding cache-busting parameter
      if (src) {
        setImageSrc(`${src}${src.includes('?') ? '&' : '?'}retry=${Date.now()}`);
      }
    }
  };

  return (
    <div className={`relative ${className}`}>
      {/* Loading State */}
      {imageState === 'loading' && (
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-900/95 via-purple-900/80 to-gray-900/95 z-10">
          <div className="flex flex-col items-center space-y-4">
            {/* Cyberpunk Loading Animation */}
            <div className="relative">
              <div className="w-16 h-16 border-4 border-purple-500/30 border-t-cyan-400 rounded-full animate-spin"></div>
              <div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-r-pink-400 rounded-full animate-spin" style={{ animationDuration: '1.5s', animationDirection: 'reverse' }}></div>
              <div className="absolute inset-2 w-12 h-12 border-2 border-transparent border-t-green-400 rounded-full animate-spin" style={{ animationDuration: '0.8s' }}></div>
            </div>
            
            {/* Matrix-style loading text */}
            <div className="text-center">
              <p className={`text-cyan-400 text-sm font-mono ${enableGlitch ? 'animate-pulse' : ''}`}>
                {loadingText}
              </p>
              <div className="flex space-x-1 mt-2">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce"
                    style={{ animationDelay: `${i * 0.2}s` }}
                  ></div>
                ))}
              </div>
            </div>
            
            {/* Glitch effect overlay */}
            {enableGlitch && (
              <div className="absolute inset-0 opacity-20">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-cyan-400/10 to-transparent animate-pulse"></div>
                <div className="absolute top-1/4 left-0 right-0 h-0.5 bg-pink-500/60 animate-pulse" style={{ animationDelay: '0.3s' }}></div>
                <div className="absolute top-3/4 left-0 right-0 h-0.5 bg-green-400/60 animate-pulse" style={{ animationDelay: '0.6s' }}></div>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Error State */}
      {imageState === 'error' && (
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-red-900/30 via-gray-900/80 to-red-900/30 z-10 border border-red-500/50">
          <div className="text-center space-y-4 p-4">
            <div className="text-red-400">
              <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <p className="text-red-300 text-sm font-mono">Neural link disrupted</p>
            <p className="text-gray-400 text-xs">Image generation failed</p>
            <button
              onClick={handleRetry}
              className="px-4 py-2 bg-red-600/20 hover:bg-red-600/40 border border-red-500/50 text-red-300 hover:text-red-100 rounded-lg transition-all text-sm font-mono"
            >
              {'>'} RETRY_CONNECTION [{retryCount + 1}]
            </button>
          </div>
        </div>
      )}
      
      {/* Skeleton/Placeholder */}
      <div className="w-full h-full bg-gradient-to-br from-gray-800 via-purple-800/20 to-gray-800 flex items-center justify-center">
        <div className="text-gray-600 text-xs font-mono opacity-50">NEURAL_INTERFACE_OFFLINE</div>
      </div>
      
      {/* Actual Image */}
      {imageSrc && (
        <img
          src={imageSrc}
          alt={alt}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-in-out ${
            imageState === 'loaded' ? 'opacity-100' : 'opacity-0'
          }`}
          onLoad={handleImageLoad}
          onError={handleImageError}
        />
      )}
      
      {/* Generation Success Indicator */}
      {imageState === 'loaded' && (
        <div className="absolute bottom-2 right-2 bg-green-600/80 text-white text-xs px-2 py-1 rounded-md backdrop-blur-sm">
          <div className="flex items-center space-x-1">
            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
            <span>NEURAL_LINK_ACTIVE</span>
          </div>
        </div>
      )}
    </div>
  );
};

interface EnhancedGameplayScreenProps {
  userProfile: UserProfile | null;
  cartridgeId: string;
  cartridgeName?: string;
  instanceId?: string;
  onExit: () => void;
  // Optional saved state for resuming games
  savedStorySegment?: StorySegment | null;
  savedChatMessages?: ChatMessage[];
  savedPlayerSession?: PlayerSession | null;
}

const EnhancedGameplayScreen: React.FC<EnhancedGameplayScreenProps> = ({
  userProfile,
  cartridgeId,
  cartridgeName = 'Story Cartridge',
  instanceId = `instance_${Date.now()}`,
  onExit,
  savedStorySegment = null,
  savedChatMessages = [],
  savedPlayerSession = null
}) => {
  // Navigation
  const navigate = useNavigate();
  
  // AI Services
  const unifiedAI = UnifiedAIService.getInstance();
  
  // Core game state - initialize from saved state if available
  const [currentStory, setCurrentStory] = useState<StorySegment | null>(savedStorySegment);
  const [playerSession, setPlayerSession] = useState<PlayerSession | null>(savedPlayerSession);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>(savedChatMessages);
  const [isLoading, setIsLoading] = useState(false);
  const [validCartridgeId, setValidCartridgeId] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Immediately validate and set cartridge ID to prevent UUID errors
  const safeCartridgeId = useMemo(() => {
    // Check if cartridgeId is a valid UUID
    const isValidUuid = cartridgeId.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i);
    if (isValidUuid) {
      return cartridgeId;
    }

    // For built-in cartridges with slug IDs, generate deterministic UUID
    // Use a namespace UUID + slug to get consistent UUID for same cartridge
    const namespace = '6ba7b810-9dad-11d1-80b4-00c04fd430c8'; // Standard namespace UUID
    const hash = cartridgeId.split('').reduce((acc, char) => {
      return ((acc << 5) - acc) + char.charCodeAt(0);
    }, 0);
    const deterministicId = `${namespace.substring(0, 28)}-${Math.abs(hash).toString(16).padStart(8, '0').substring(0,8)}`;

    console.log(`[EnhancedGameplay] Converting slug "${cartridgeId}" to UUID: ${deterministicId}`);
    return deterministicId;
  }, [cartridgeId]);
  
  // Git-based game state
  const [currentBranchId, setCurrentBranchId] = useState<string | null>(null);
  const [mergeOpportunities, setMergeOpportunities] = useState<any[]>([]);
  const [worldKarma, setWorldKarma] = useState<number>(0);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [showGitTree, setShowGitTree] = useState(false);
  
  // Year Zero Engine integration
  const [yearZeroCharacter, setYearZeroCharacter] = useState<any>(null);
  const [currentStress, setCurrentStress] = useState(0);
  const [activeMutations, setActiveMutations] = useState<any[]>([]);
  
  // AI Agents state (being replaced by dynamic companions)
  const [availableAgents, setAvailableAgents] = useState<AIAgent[]>([]);
  const [activeAgent, setActiveAgent] = useState<AIAgent | null>(null);
  const [agentResponse, setAgentResponse] = useState<string>('');
  const [narrationUrl, setNarrationUrl] = useState<string | null>(null);
  const [quests, setQuests] = useState<Quest[]>([]);

  // Dynamic companion and location system
  const [currentLocation, setCurrentLocation] = useState<string>('Unknown Location');
  const [dynamicCompanions, setDynamicCompanions] = useState<DynamicCompanion[]>([]);
  const [nearbyPlayers, setNearbyPlayers] = useState<PlayerLocation[]>([]);
  const [selectedCompanion, setSelectedCompanion] = useState<DynamicCompanion | null>(null);

  // Mutation system
  const [playerMutations, setPlayerMutations] = useState<Mutation[]>([]);
  const [showMutationModal, setShowMutationModal] = useState(false);
  const [newMutation, setNewMutation] = useState<Mutation | null>(null);

  // Display mode toggle (text vs graphical)
  const [displayMode, setDisplayMode] = useState<'text' | 'graphical'>('text');

  const playMessageTTS = async (text: string) => {
    try {
      await safeTTSService.textToSpeech(text, 'medium');
    } catch (e) {
      console.warn('[Audio] Message TTS failed', e);
    }
  };
  
  // Karma and choice state
  const [karmaHistory, setKarmaHistory] = useState<KarmaImpact[]>([]);
  const [selectedChoice, setSelectedChoice] = useState<string>('');
  const [customChoice, setCustomChoice] = useState<string>('');
  const [companionMessage, setCompanionMessage] = useState<string>('');
  const [isGeneratingResponse, setIsGeneratingResponse] = useState(false);

  // Immediately mark as initialized if we have saved data
  useEffect(() => {
    if (savedStorySegment && savedPlayerSession && !isInitialized) {
      console.log('[EnhancedGameplay] Resuming from saved state');

      // CRITICAL: Restore branchId from saved session
      if (savedPlayerSession.gameState?.branchId) {
        setCurrentBranchId(savedPlayerSession.gameState.branchId);
        console.log('[EnhancedGameplay] Restored branchId:', savedPlayerSession.gameState.branchId);
      } else {
        console.warn('[EnhancedGameplay] No branchId in saved session, creating new branch...');
        // Ensure cartridge exists before creating branch (to satisfy foreign key)
        (async () => {
          try {
            // Check/create cartridge first
            const { data: existing } = await supabaseClient
              .from('user_created_cartridges')
              .select('id')
              .eq('id', cartridgeId)
              .maybeSingle();

            if (!existing) {
              console.log('[EnhancedGameplay] Creating cartridge record for branch...');
              await supabaseClient
                .from('user_created_cartridges')
                .insert({
                  id: cartridgeId,
                  cartridge_id: `cartridge-${cartridgeId.substring(0, 8)}`,
                  creator_id: userProfile?.id || userProfile?.uid,
                  user_id: userProfile?.uid,
                  author_id: userProfile?.uid,
                  name: 'Resumed Game Cartridge',
                  description: 'Auto-created for session resume',
                  version: '1.0.0',
                  archetypes: [],
                  companions: [],
                  world_settings: { genre: 'cyberpunk', setting: 'Neo-Veridia' },
                  story_prompts: [],
                  is_public: false,
                  is_built_in: false
                });
            }

            // Now create branch
            const branch = await gitGameStateService.createBranch(
              savedPlayerSession.gameState?.genre || 'cyberpunk',
              cartridgeId,
              userProfile?.uid || 'demo-user',
              userProfile?.name || 'Dreamer'
            );
            setCurrentBranchId(branch.id);
            console.log('[EnhancedGameplay] Created new branch:', branch.id);
          } catch (error) {
            console.error('[EnhancedGameplay] Failed to create branch:', error);
          }
        })();
      }

      // Restore world karma if available
      if (savedPlayerSession.gameState?.worldKarma !== undefined) {
        setWorldKarma(savedPlayerSession.gameState.worldKarma);
      }

      setIsInitialized(true);
    }
  }, [savedStorySegment, savedPlayerSession, isInitialized]);

  // Initialize game session with git branch - ONLY run once when component mounts
  useEffect(() => {
    console.log('[EnhancedGameplay] useEffect triggered:', {
      isInitialized,
      hasUserProfile: !!userProfile?.uid,
      cartridgeId,
      isLoading,
      hasSavedData: !!(savedStorySegment && savedPlayerSession)
    });

    if (!isInitialized && userProfile?.uid && !isLoading && !savedStorySegment) {
      console.log('[EnhancedGameplay] Starting initialization...');
      initializeGameSession();
    }
  }, [userProfile?.uid, cartridgeId, isInitialized, isLoading, savedStorySegment]); // Only depend on stable identifiers

  // Dynamic location and companion spawning - triggers when story changes
  useEffect(() => {
    if (!currentStory || !playerSession || !userProfile) return;

    const handleLocationChange = async () => {
      // Extract location from narrative
      const newLocation = locationTrackingService.extractLocationFromNarrative(currentStory.narrative);

      if (newLocation !== currentLocation) {
        console.log(`[EnhancedGameplay] Location changed: ${currentLocation} → ${newLocation}`);
        setCurrentLocation(newLocation);

        // Update player location in database for multiplayer
        await locationTrackingService.updatePlayerLocation(
          userProfile.uid,
          userProfile.name,
          newLocation,
          newLocation,
          playerSession.gameState?.roomId || null,
          'exploring',
          playerSession.currentKarma
        );

        // Remove companions that should leave
        const updatedCompanions = dynamicCompanions.filter(companion =>
          !dynamicCompanionService.shouldCompanionLeave(companion, newLocation, [])
        );

        // Generate new companions for this location (1-2 companions)
        const companionCount = 1 + Math.floor(Math.random() * 2);
        const newCompanions = await dynamicCompanionService.generateCompanionsForScene(
          newLocation,
          worldKarma,
          playerSession.currentKarma,
          companionCount
        );

        setDynamicCompanions([...updatedCompanions, ...newCompanions]);

        // Query for nearby players in same location
        const playersHere = await locationTrackingService.getPlayersAtLocation(
          newLocation,
          playerSession.gameState?.roomId,
          userProfile.uid
        );
        setNearbyPlayers(playersHere);

        console.log(`[EnhancedGameplay] Spawned ${newCompanions.length} companions, found ${playersHere.length} nearby players`);
      }
    };

    handleLocationChange();
  }, [currentStory?.narrative]); // Trigger when story narrative changes

  // Update session persistence with current game state - separate effect
  useEffect(() => {
    const gameState = {
      currentScreen: 'gameplay' as const,
      userProfile,
      currentPlayerState: playerSession,
      currentStorySegment: currentStory,
      selectedCartridge: { id: cartridgeId, name: 'Current Game' },
      currentRoom: null,
      isMultiplayer: false,
      isLoading: false,
      isLoadingChat: false,
      isLoadingAppearance: false,
      isListening: false,
      microphoneError: null,
      error: null,
      apiKeyStatus: 'configured' as const,
      chatMessages,
      currentSessionId: playerSession?.sessionId || null,
      isProfileLoading: false,
      worldKarma,
      currentBranchId
    };
    
    sessionPersistenceService.updateCurrentSession(gameState);
  }, [userProfile, playerSession, currentStory, chatMessages, worldKarma, currentBranchId]);

  // Check for merge opportunities periodically
  useEffect(() => {
    const interval = setInterval(async () => {
      if (currentBranchId) {
        await checkMergeOpportunities();
      }
    }, 30000); // Check every 30 seconds
    
    return () => clearInterval(interval);
  }, [currentBranchId]);

  // Load relevant agents when karma or location changes
  useEffect(() => {
    if (playerSession) {
      loadRelevantAgents();
    }
  }, [playerSession?.currentKarma, playerSession?.location]);

  // Check for genre shifts based on collective karma
  useEffect(() => {
    if (playerSession && currentBranchId) {
      checkGenreShift();
    }
  }, [playerSession?.currentKarma]);

  // Start location heartbeat and cleanup on unmount
  useEffect(() => {
    if (userProfile?.uid) {
      locationTrackingService.startHeartbeat(userProfile.uid);
    }

    return () => {
      locationTrackingService.stopHeartbeat();
      if (userProfile?.uid) {
        locationTrackingService.removePlayer(userProfile.uid);
      }
    };
  }, [userProfile?.uid]);

  const initializeGameSession = async () => {
    console.log('[EnhancedGameplay] initializeGameSession called:', {
      hasUserProfile: !!userProfile,
      isLoading,
      isInitialized,
      hasSavedStory: !!savedStorySegment,
      hasSavedSession: !!savedPlayerSession
    });

    // If we have saved data, use it instead of initializing new session
    if (savedStorySegment && savedPlayerSession) {
      console.log('[EnhancedGameplay] Using saved game state, skipping initialization');
      setIsInitialized(true);
      return;
    }

    if (!userProfile || isLoading || isInitialized) {
      console.log('[EnhancedGameplay] Skipping initialization - conditions not met');
      return;
    }

    try {
      console.log('[EnhancedGameplay] Starting initialization process...');
      setIsLoading(true);
      
      // Use the pre-validated safe cartridge ID
      const resolvedCartridgeId = safeCartridgeId;
      
      // Store the valid cartridge ID in state
      setValidCartridgeId(resolvedCartridgeId);
      
      // Ensure the cartridge exists in the database - THIS IS CRITICAL FOR FOREIGN KEY CONSTRAINT
      try {
        // First check if cartridge exists by ID (primary key)
        const { data: existingCartridge, error: checkError } = await supabaseClient
          .from('user_created_cartridges')
          .select('id')
          .eq('id', resolvedCartridgeId)
          .maybeSingle(); // Use maybeSingle() to avoid 406 errors when row doesn't exist

        if (!existingCartridge) {
          // Cartridge doesn't exist, create it
          const { data: newCartridge, error: insertError } = await supabaseClient
            .from('user_created_cartridges')
            .insert({
              id: resolvedCartridgeId,
              cartridge_id: `cartridge-${resolvedCartridgeId.substring(0, 8)}`, // Different from primary key
              creator_id: userProfile.id, // Use user profile ID
              user_id: userProfile.uid, // Also set auth user ID
              author_id: userProfile.uid, // Set author_id for auth constraint
              name: 'Lucid Cyber RPG',
              description: 'A cyberpunk adventure in Neo-Tokyo',
              version: '1.0.0',
              archetypes: [
                {
                  id: 'cyberpunk-hacker',
                  name: 'Cyberpunk Hacker',
                  description: 'A skilled netrunner navigating the digital underworld'
                }
              ],
              companions: [],
              world_settings: {
                genre: 'cyberpunk',
                setting: 'Neo-Tokyo 2085'
              },
              story_prompts: [
                'The neon lights of Neo-Tokyo flicker as you jack into the matrix...'
              ],
              is_public: true,
              is_built_in: false
            })
            .select('id')
            .single();
            
          if (insertError) {
            console.error('[EnhancedGameplay] Failed to create cartridge:', insertError);
            throw insertError;
          }
          
          console.log('[EnhancedGameplay] Created new cartridge:', newCartridge);
        } else if (checkError) {
          console.error('[EnhancedGameplay] Error checking cartridge:', checkError);
          throw checkError;
        } else {
          console.log('[EnhancedGameplay] Using existing cartridge:', existingCartridge);
        }
      } catch (cartridgeError) {
        console.error('[EnhancedGameplay] Critical cartridge error:', cartridgeError);
        // This is now a critical error - we cannot proceed without a valid cartridge
        // because the foreign key constraint will fail
        throw new Error(`Failed to ensure cartridge exists: ${cartridgeError.message}`);
      }
      
      // Create git branch for this game session
      const branch = await gitGameStateService.createBranch(
        'cyberpunk', // Initial genre
        resolvedCartridgeId,
        userProfile.uid || 'demo-user',
        userProfile.name
      );
      
      setCurrentBranchId(branch.id);
      
      // Initialize Year Zero character
      const yzCharacter = yearZeroEngine.createCharacter(
        userProfile.name,
        'cyberpunk-hacker',
        0
      );
      setYearZeroCharacter(yzCharacter);
      setCurrentStress(yzCharacter.stress);
      setActiveMutations(yzCharacter.mutations.filter(m => m.isActive));
      
      // Load world karma state
      const worldStats = await gitGameStateService.getCartridgeStats(resolvedCartridgeId);
      setWorldKarma(worldStats.averageKarma);
      
      // Create initial player session
      const session: PlayerSession = {
        id: uuidv4(),
        userId: userProfile.uid || 'demo-user',
        cartridgeId: resolvedCartridgeId,
        archetypeId: 'cyberpunk-hacker',
        characterName: userProfile.name,
        currentKarma: 0,
        karmaHistory: [],
        location: 'Neo-Tokyo Central Plaza',
        gameState: {
          branchId: branch.id,
          genre: branch.genre,
          worldKarma: worldStats.averageKarma
        },
        storyHistory: [],
        isActive: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      setPlayerSession(session);

      // Initialize story dynamically via OpenRouter with fallback
      let initialStory: StorySegment;
      try {
        console.log('[EnhancedGameplay] [Dynamic] Generating initial narrative via OpenRouter...');
        const dyn = await generateDynamicStorySegment(
          `You arrive in Neo-Tokyo Central Plaza. World karma: ${worldStats.averageKarma.toFixed(1)}.`,
          'Begin the adventure',
          'New session start',
          'Cyberpunk Hacker archetype focused on digital infiltration and social engineering',
          userProfile,
          userProfile.uid
        );

        const scenePrompt = dyn.image_prompt || 'cyberpunk cityscape, neon lights, rain, crowd, data streams';
        const dynImageUrl = await unifiedAI.generateImage(scenePrompt, 'cyberpunk');

        initialStory = {
          id: uuidv4(),
          narrative: dyn.narrative,
          imagePrompt: scenePrompt,
          imageUrl: dynImageUrl || undefined,
          choices: (dyn.choices && dyn.choices.length >= 3) ? dyn.choices.slice(0, 3) : [
            'Investigate the mysterious data breach at MegaCorp',
            'Help a street vendor being harassed by corpo security',
            'Dive into the underground hacker network for information'
          ],
          companionMessages: [
            ...(dyn.companion_dialogue || []),
            { name: 'System', dialogue: `Neural interface initialized. Git branch: ${branch.branchData.name}` },
            { name: 'World', dialogue: `Collective Karma: ${worldStats.averageKarma.toFixed(1)} | Active Players: ${worldStats.playerCount}` }
          ]
        };
      } catch (e) {
        console.warn('[EnhancedGameplay] [Dynamic] Initial narrative generation failed, using fallback:', e);
        const initialImagePrompt = 'cyberpunk cityscape with neon lights, futuristic buildings, digital screens, rain-soaked streets, person in tech gear';
        const initialImageUrl = await unifiedAI.generateImage(initialImagePrompt, 'cyberpunk');
        initialStory = {
          id: uuidv4(),
          narrative: `Welcome to Neo-Tokyo, ${userProfile.name}. The neon-lit streets pulse with digital energy as you stand at the threshold of your cyber-adventure. World karma reads ${worldStats.averageKarma.toFixed(1)}.`,
          imagePrompt: initialImagePrompt,
          imageUrl: initialImageUrl || undefined,
          choices: [
            'Investigate the mysterious data breach at MegaCorp',
            'Help a street vendor being harassed by corpo security',
            'Dive into the underground hacker network for information'
          ],
          companionMessages: [
            { name: 'System', dialogue: `Neural interface initialized. Git branch: ${branch.branchData.name}` },
            { name: 'World', dialogue: `Collective Karma: ${worldStats.averageKarma.toFixed(1)} | Active Players: ${worldStats.playerCount}` }
          ]
        };
      }

      setCurrentStory(initialStory);
      // Generate narration audio for initial story (best-effort)
      try {
        await safeTTSService.textToSpeech(initialStory.narrative, 'high');
      } catch (e) {
        console.warn('[Audio] Initial narration TTS failed', e);
      }

      // Generate quests from initial story
      try {
        const uid = userProfile.uid || 'demo-user';
        const q = questService.generateFromStorySegment(uid, resolvedCartridgeId, initialStory);
        setQuests(q);
      } catch (e) {
        console.warn('[Quest] generation failed', e);
      }

      // Add welcome message with git context
      const welcomeMessage: ChatMessage = {
        id: uuidv4(),
        sender: 'System',
        text: `Welcome, ${userProfile.name}! Your story branch has been created. Your decisions will be recorded as commits in the collective narrative. Current world karma: ${worldStats.averageKarma.toFixed(1)}`,
        timestamp: Date.now()
      };

      setChatMessages([welcomeMessage]);

    } catch (error) {
      console.error('[EnhancedGameplay] Initialization error:', error);
      
      // Show user-friendly error message
      setCurrentStory({
        narrative: `⚠️ Failed to initialize game session: ${error instanceof Error ? error.message : 'Unknown error'}
        
This could be due to:
• Database connectivity issues
• Missing cartridge data
• Authentication problems

Please try refreshing the page or contact support if this persists.`,
        imagePrompt: 'error screen with cyberpunk aesthetics, warning symbols, glitchy digital interface',
        choices: ['Retry Initialization', 'Return to Menu'],
        companionMessages: [
          { name: 'System', dialogue: 'Neural interface connection failed. Diagnostics available.' }
        ]
      });
    } finally {
      setIsLoading(false);
      setIsInitialized(true);
    }
  };

  const loadRelevantAgents = async () => {
    if (!playerSession) return;

    try {
      const agents = await AIAgentService.getRelevantAgents(
        playerSession.location,
        playerSession.currentKarma
      );
      setAvailableAgents(agents);

      // Auto-select agent based on karma alignment
      if (agents.length > 0 && !activeAgent) {
        const bestAgent = agents.find(a => 
          Math.abs(a.karmaThreshold - playerSession.currentKarma) < 10
        ) || agents[0];
        setActiveAgent(bestAgent);
      }
    } catch (error) {
      console.error('[EnhancedGameplay] Agent loading error:', error);
    }
  };

  const handleChoice = async (choice: string) => {
    console.log('[EnhancedGameplay] Choice clicked:', choice);

    // Handle special choices for error recovery
    if (choice === 'Retry Initialization') {
      await initializeGameSession();
      return;
    }

    if (choice === 'Return to Menu') {
      navigate('/');
      return;
    }

    if (!playerSession || isGeneratingResponse || !currentBranchId) {
      console.warn('[EnhancedGameplay] Choice blocked:', {
        hasPlayerSession: !!playerSession,
        isGenerating: isGeneratingResponse,
        hasBranchId: !!currentBranchId
      });
      return;
    }

    try {
      console.log('[EnhancedGameplay] Processing choice...');
      setIsGeneratingResponse(true);
      setSelectedChoice(choice);

      const existingKarmaHistory = Array.isArray(playerSession.karmaHistory)
        ? playerSession.karmaHistory
        : [];

      // Calculate karma impact
      const storyContext: StoryContext = {
        playerLevel: 1,
        location: playerSession.location,
        recentHistory: existingKarmaHistory.slice(-5).map(k => k.description || ''),
        worldKarma,
        cartridgeSettings: {
          karmaWeights: {
            altruistic: 1.0,
            selfish: -1.0,
            violent: -1.5,
            peaceful: 1.0,
            leadership: 0.5,
            cooperation: 1.0
          },
          karmaThresholds: {
            major: 50,
            moderate: 25,
            minor: 10
          }
        }
      };
      
      const karmaImpact = KarmaEngine.calculateKarmaImpact(choice, storyContext);

      // Update player karma
      const oldKarma = playerSession.currentKarma;
      const newKarma = playerSession.currentKarma + karmaImpact.personalDelta;
      const updatedSession = {
        ...playerSession,
        currentKarma: newKarma,
        karmaHistory: [...existingKarmaHistory, karmaImpact]
      };
      setPlayerSession(updatedSession);
      setKarmaHistory(prev => [...prev, karmaImpact]);

      // Check for mutation acquisition (karma milestone crossed)
      const mutationMilestone = mutationService.checkMutationMilestone(oldKarma, newKarma);
      if (mutationMilestone && userProfile) {
        const existingMutationIds = playerMutations.map(m => m.id);
        const availableMutation = mutationService.getRandomMutationForKarma(newKarma, existingMutationIds);

        if (availableMutation) {
          setNewMutation(availableMutation);
          setShowMutationModal(true);
          console.log('[EnhancedGameplay] Mutation unlocked:', availableMutation.name);

          // Show mutation notification
          const mutationMessage: ChatMessage = {
            id: uuidv4(),
            sender: 'System',
            text: `✨ Mutation Unlocked: ${availableMutation.name}! Your karma has opened new evolutionary paths...`,
            timestamp: Date.now()
          };
          setChatMessages(prev => [...prev, mutationMessage]);
        }
      }

      // Award experience for making a story choice
      if (userProfile) {
        const experienceGained = levelUpService.calculateExperienceGain('story_choice', {
          karmaBonus: Math.abs(karmaImpact.personalDelta),
          difficulty: choice.length > 100 ? 'hard' : 'medium',
          isFirstTime: false
        });
        
        const levelUpEvent = await levelUpService.awardExperience(
          userProfile, 
          experienceGained, 
          'story_choice'
        );
        
        if (levelUpEvent) {
          // Show level up notification
          const levelUpMessage: ChatMessage = {
            id: uuidv4(),
            sender: 'System',
            text: `🎉 Level Up! You reached level ${levelUpEvent.newLevel}! Rewards: ${levelUpEvent.rewards.map(r => r.name).join(', ')}`,
            timestamp: Date.now()
          };
          setChatMessages(prev => [...prev, levelUpMessage]);
          
          // Play level up sound effect
          soundscapeService.playSoundEffect('level_up', 0.8);
        }
      }

      // Record choice as git commit (Supabase)
      const commit = await gitGameStateService.commitPlayerChoice(
        currentBranchId,
        playerSession.userId,
        playerSession.characterName,
        {
          segmentId: `segment_${Date.now()}`,
          choiceText: choice,
          consequences: karmaImpact.reasoning,
          karmaImpact: karmaImpact.personalDelta
        }
      );

      // ALSO commit to public GitHub repository (background, non-blocking)
      // Solo instances stay private, multiplayer instances go to GitHub
      const isSoloRun = !instanceId || instanceId.startsWith('instance_');
      githubWorldStateService.createPlayerChoiceCommit({
        playerId: playerSession.userId || userProfile?.uid || null,
        playerName: playerSession.characterName,
        choice: choice,
        karma: newKarma,
        location: currentLocation,
        consequences: karmaImpact.reasoning,
        cartridgeId: cartridgeId,
        cartridgeName: cartridgeName,
        instanceId: instanceId,
        isSolo: isSoloRun
      }).catch(err => {
        console.warn('[EnhancedGameplay] GitHub commit queued (will sync in background):', err);
      });

      // Check for world state changes
      if (commit.worldStateChanges && commit.worldStateChanges.length > 0) {
        const worldChangeMessage: ChatMessage = {
          id: uuidv4(),
          sender: 'World',
          text: `⚡ World Event: ${commit.worldStateChanges.join(', ')}`,
          timestamp: Date.now()
        };
        setChatMessages(prev => [...prev, worldChangeMessage]);
      }

      // Update world karma
      if (validCartridgeId) {
        const newWorldStats = await gitGameStateService.getCartridgeStats(validCartridgeId);
        setWorldKarma(newWorldStats.averageKarma);
      }

      // Get AI agent response if active
      if (activeAgent) {
        const response = await AIAgentService.getAgentResponse(
          activeAgent,
          choice,
          karmaImpact
        );
        setAgentResponse(response);

        const agentMessage: ChatMessage = {
          id: uuidv4(),
          sender: activeAgent.name,
          text: response,
          timestamp: Date.now()
        };
        setChatMessages(prev => [...prev, agentMessage]);
      }

      // Generate next story segment
      await generateNextStorySegment(choice, karmaImpact, updatedSession);

      // Add choice message
      const choiceMessage: ChatMessage = {
        id: uuidv4(),
        sender: playerSession.characterName,
        text: `> ${choice}`,
        timestamp: Date.now()
      };
      setChatMessages(prev => [...prev, choiceMessage]);

      // Check for merge opportunities
      await checkMergeOpportunities();

    } catch (error) {
      console.error('[EnhancedGameplay] Choice handling error:', error);
    } finally {
      setIsGeneratingResponse(false);
      setSelectedChoice('');
    }
  };

  const handleCompanionChat = async (message: string) => {
    if (!activeAgent || !playerSession || isGeneratingResponse) return;

    try {
      setIsGeneratingResponse(true);
      setCompanionMessage('');

      // Add player's message to chat
      const playerMsg: ChatMessage = {
        id: uuidv4(),
        sender: playerSession.characterName,
        text: message,
        timestamp: Date.now()
      };
      setChatMessages(prev => [...prev, playerMsg]);

      // Get agent response with realm influence
      const response = await AIAgentService.getAgentResponse(
        activeAgent,
        message,
        0 // No direct karma impact from chat
      );

      // Parse influence from response (looking for karma modifiers)
      let realmInfluence = 0;
      if (response.toLowerCase().includes('positive') || response.toLowerCase().includes('good')) {
        realmInfluence = 0.5;
      } else if (response.toLowerCase().includes('negative') || response.toLowerCase().includes('dark')) {
        realmInfluence = -0.5;
      }

      // Apply subtle realm influence
      if (realmInfluence !== 0) {
        const newWorldKarma = Math.max(-10, Math.min(10, worldKarma + realmInfluence));
        setWorldKarma(newWorldKarma);

        // Update player session with influenced karma
        const updatedSession = await sessionService.updateSession(playerSession.id, {
          currentKarma: Math.max(-10, Math.min(10, playerSession.currentKarma + (realmInfluence * 0.2)))
        });
        setPlayerSession(updatedSession);

        // Add realm influence notification
        const influenceMsg: ChatMessage = {
          id: uuidv4(),
          sender: 'System',
          text: `[Realm responds to your conversation - karma shifted by ${realmInfluence > 0 ? '+' : ''}${realmInfluence}]`,
          timestamp: Date.now()
        };
        setChatMessages(prev => [...prev, influenceMsg]);
      }

      // Add agent response to chat
      const agentMsg: ChatMessage = {
        id: uuidv4(),
        sender: activeAgent.name,
        text: response,
        timestamp: Date.now()
      };
      setChatMessages(prev => [...prev, agentMsg]);

      // Occasionally trigger story events based on conversation
      if (Math.random() < 0.3) { // 30% chance
        const eventMsg: ChatMessage = {
          id: uuidv4(),
          sender: 'Narrator',
          text: `[Your conversation with ${activeAgent.name} echoes through the digital realm, subtly altering the narrative...]`,
          timestamp: Date.now()
        };
        setChatMessages(prev => [...prev, eventMsg]);

        // Trigger a subtle story update
        await generateNextStorySegment(
          `Continue after the conversation with ${activeAgent.name} about: ${message.substring(0, 50)}...`,
          realmInfluence,
          playerSession
        );
      }

    } catch (error) {
      console.error('[EnhancedGameplay] Companion chat error:', error);
    } finally {
      setIsGeneratingResponse(false);
    }
  };

  const handleMutationAcquisition = async (mutation: Mutation) => {
    if (!userProfile) return;

    try {
      // Save mutation to database
      const { error } = await supabaseClient
        .from('player_mutations')
        .insert({
          player_id: userProfile.uid,
          mutation_id: mutation.id,
          karma_at_acquisition: playerSession?.currentKarma || 0,
          level: 1
        });

      if (!error) {
        setPlayerMutations(prev => [...prev, mutation]);
        console.log('[EnhancedGameplay] Mutation acquired:', mutation.name);

        // Show success message
        const successMsg: ChatMessage = {
          id: uuidv4(),
          sender: 'System',
          text: `🧬 ${mutation.name} acquired! ${mutation.visual}`,
          timestamp: Date.now()
        };
        setChatMessages(prev => [...prev, successMsg]);
      }
    } catch (error) {
      console.error('[EnhancedGameplay] Mutation acquisition error:', error);
    } finally {
      setShowMutationModal(false);
      setNewMutation(null);
    }
  };

  const checkMergeOpportunities = async () => {
    if (!currentBranchId || !playerSession) return;

    try {
      const opportunities = await gitGameStateService.findMergeableBranches(
        currentBranchId,
        10 // Max karma difference
      );

      setMergeOpportunities(opportunities);

      // AUTOMATIC MERGING: Merge the best opportunity automatically
      if (opportunities.length > 0) {
        // Sort by karma similarity (closest karma = best merge candidate)
        const sortedOpportunities = opportunities.sort((a, b) => a.karmaDifference - b.karmaDifference);
        const bestMerge = sortedOpportunities[0];

        // Auto-merge if karma difference is very small (< 3) or has shared commits
        if (bestMerge.karmaDifference < 3 || bestMerge.sharedCommits > 0) {
          console.log('[EnhancedGameplay] Auto-merging with similar karma branch:', bestMerge.branchData.name);

          if (uuidValidate(bestMerge.branchId)) {
            // Perform automatic merge
            await handleMerge(bestMerge.branchId);
          } else {
            console.warn('[EnhancedGameplay] Skipping auto-merge due to invalid branch id format:', bestMerge.branchId);
          }

          // Notify player
          const mergeMsg: ChatMessage = {
            id: uuidv4(),
            sender: 'Narrator',
            text: `[Your story converges with another player's path (Karma Δ: ${bestMerge.karmaDifference.toFixed(1)}). The timeline merges...]`,
            timestamp: Date.now()
          };
          setChatMessages(prev => [...prev, mergeMsg]);
        } else {
          // Show as optional merge for larger karma differences
          if (Math.random() < 0.2) { // 20% chance to show modal for distant merges
            setShowMergeModal(true);
          }
        }
      }
    } catch (error) {
      console.error('[EnhancedGameplay] Merge check error:', error);
    }
  };

  const handleMerge = async (targetBranchId: string) => {
    if (!currentBranchId || !playerSession) return;
    if (!uuidValidate(targetBranchId)) {
      console.warn('[EnhancedGameplay] Merge skipped due to invalid branch id:', targetBranchId);
      return;
    }

    try {
      const result = await gitGameStateService.mergeBranches(
        currentBranchId,
        targetBranchId,
        playerSession.userId,
        'Player stories converging through karmic alignment'
      );
      
      if (result.status === 'success' && result.mergedBranch) {
        setCurrentBranchId(result.mergedBranch.id);
        
        const mergeMessage: ChatMessage = {
          id: uuidv4(),
          sender: 'System',
          text: `🔀 Stories Merged! Your narrative has converged with another player's branch. Conflicts resolved: ${result.conflictsResolved}`,
          timestamp: Date.now()
        };
        setChatMessages(prev => [...prev, mergeMessage]);
      }
      
      setShowMergeModal(false);
    } catch (error) {
      console.error('[EnhancedGameplay] Merge error:', error);
    }
  };

  const checkGenreShift = async () => {
    if (!playerSession || !currentBranchId || !validCartridgeId) return;
    
    try {
      const stats = await gitGameStateService.getCartridgeStats(validCartridgeId);
      
      // Check if collective karma triggers genre shift
      if (stats.averageKarma > 75 && stats.playerCount > 10) {
        if (playerSession.gameState?.genre !== 'solarpunk') {
          // Shift to solarpunk
          await gitGameStateService.mutateGenre(
            validCartridgeId,
            'cyberpunk',
            'solarpunk',
            'Collective enlightenment transforms the digital realm'
          );
          
          setPlayerSession(prev => prev ? {
            ...prev,
            gameState: { ...prev.gameState, genre: 'solarpunk' }
          } : null);
          
          const shiftMessage: ChatMessage = {
            id: uuidv4(),
            sender: 'World',
            text: '🌟 Reality Shift! The collective consciousness has transformed the world into a Solarpunk utopia!',
            timestamp: Date.now()
          };
          setChatMessages(prev => [...prev, shiftMessage]);
        }
      } else if (stats.averageKarma < -75 && stats.playerCount > 10) {
        if (playerSession.gameState?.genre !== 'grimdark') {
          // Shift to grimdark
          await gitGameStateService.mutateGenre(
            validCartridgeId,
            'cyberpunk',
            'grimdark',
            'Collective darkness consumes the digital realm'
          );
          
          setPlayerSession(prev => prev ? {
            ...prev,
            gameState: { ...prev.gameState, genre: 'grimdark' }
          } : null);
          
          const shiftMessage: ChatMessage = {
            id: uuidv4(),
            sender: 'World',
            text: '💀 Reality Shift! The collective consciousness has plunged the world into Grimdark despair!',
            timestamp: Date.now()
          };
          setChatMessages(prev => [...prev, shiftMessage]);
        }
      }
    } catch (error) {
      console.error('[EnhancedGameplay] Genre shift check error:', error);
    }
  };

  const generateNextStorySegment = async (
    choice: string,
    karmaImpact: KarmaImpact,
    session: PlayerSession
  ) => {
    // Prefer dynamic generation via OpenRouter, with robust fallback to previous deterministic path
    try {
      const prevNarrative = currentStory?.narrative || 'You stand amid neon reflections.';
      const historySummary = (playerSession?.storyHistory || [])
        .map(s => s.narrative)
        .slice(-3)
        .join('\n');

      console.log('[EnhancedGameplay] [Dynamic] Generating next segment via OpenRouter...');
      const dyn = await generateDynamicStorySegment(
        prevNarrative,
        choice,
        historySummary,
        session.archetypeId || 'Cyberpunk Hacker archetype',
        userProfile,
        userProfile?.uid
      );

      const nextPrompt = dyn.image_prompt || `${session.gameState?.genre || 'cyberpunk'} scene, ${session.location}`;
      const nextImageUrl = await unifiedAI.generateImage(nextPrompt, session.gameState?.genre || 'cyberpunk');

      const newStory: StorySegment = {
        id: uuidv4(),
        narrative: dyn.narrative,
        imagePrompt: nextPrompt,
        imageUrl: nextImageUrl || undefined,
        choices: (dyn.choices && dyn.choices.length) ? dyn.choices.slice(0, 3) : generateKarmaInfluencedChoices(session.currentKarma, worldKarma, session.gameState?.genre || 'cyberpunk').slice(0, 3),
        companionMessages: dyn.companion_dialogue || []
      };

      setCurrentStory(newStory);
      setPlayerSession(prev => prev ? { ...prev, storyHistory: [...(prev.storyHistory || []), newStory] } : prev);
      try {
        await safeTTSService.textToSpeech(newStory.narrative, 'high');
      } catch (e) {
        console.warn('[Audio] Next segment TTS failed', e);
      }

      // Also surface companion dialogue into chat feed
      if (newStory.companionMessages?.length) {
        setChatMessages(prev => [
          ...prev,
          ...newStory.companionMessages.map((m) => ({ id: uuidv4(), sender: m.name || 'Companion', text: m.dialogue, timestamp: Date.now() }))
        ]);
      }
      
      // Generate autonomous companion responses based on story context
      setTimeout(async () => {
        try {
          const companionIds = ['jax', 'lyra'];
          for (const companionId of companionIds) {
            const contextualResponse = await autonomousEcosystemService.getContextualCompanionResponse(
              companionId,
              `Story: ${newStory.narrative.substring(0, 200)}... Player choice: ${choice}`
            );
            
            if (contextualResponse && !contextualResponse.includes('Systems are recalibrating') && !contextualResponse.includes('dream state seems unstable')) {
              setChatMessages(prev => [
                ...prev,
                { 
                  id: uuidv4(), 
                  sender: companionId.toUpperCase(), 
                  text: contextualResponse, 
                  timestamp: Date.now() 
                }
              ]);
            }
          }
        } catch (error) {
          console.error('[EnhancedGameplay] Failed to generate autonomous companion responses:', error);
        }
      }, 2000); // Delay to make it feel natural

      // Update location using existing heuristic
      updateLocationBasedOnState(session, session.gameState?.genre || 'cyberpunk');

      // Generate quests from new story segment
      try {
        const uid = userProfile?.uid || 'demo-user';
        const cid = session.cartridgeId;
        const q = questService.generateFromStorySegment(uid, cid, newStory);
        setQuests(q);
      } catch (e) {
        console.warn('[Quest] generation failed', e);
      }
    } catch (err) {
      console.warn('[EnhancedGameplay] [Dynamic] Next segment generation failed, using fallback:', err);
      // Fallback: deterministic branch based on karma/world state
      const genre = session.gameState?.genre || 'cyberpunk';
      const worldInfluence = worldKarma > 0 ? 'hopeful' : worldKarma < 0 ? 'dystopian' : 'balanced';
      const narratives = {
        positive: {
          cyberpunk: `Your compassionate choice resonates through the digital realm. The ambient lighting seems warmer, and passing citizens nod with respect. Your actions have created ripples of positive energy in Neo-Tokyo.`,
          solarpunk: `Your choice harmonizes with the sustainable ethos of the transformed city. Solar panels gleam brighter, and vertical gardens seem to bloom in response to your positive energy.`,
          grimdark: `Even in this darkened world, your spark of compassion cuts through the shadows. It's a small light, but it matters.`
        },
        negative: {
          cyberpunk: `Your selfish choice sends dark ripples through the matrix. The neon lights flicker ominously, and shadows seem to gather around you.`,
          solarpunk: `Your discordant choice creates static in the harmonious city. The living walls seem to recoil, and the solar grid dims momentarily.`,
          grimdark: `Your dark choice feeds the consuming shadows. The world grows a little colder, a little more hopeless.`
        },
        neutral: {
          cyberpunk: `Your pragmatic choice maintains the delicate balance of Neo-Tokyo. The city continues its relentless pulse.`,
          solarpunk: `Your measured response flows with the city's sustainable rhythms. Balance is maintained.`,
          grimdark: `Your choice neither saves nor damns. In this dark world, perhaps that's the best one can hope for.`
        }
      } as const;

      const karmaLevel = karmaImpact.personalDelta > 5 ? 'positive' : karmaImpact.personalDelta < -5 ? 'negative' : 'neutral';
      const narrative = (narratives as any)[karmaLevel][genre] || (narratives as any)[karmaLevel].cyberpunk;
      const worldNarrative = `\n\n[World Karma: ${worldKarma.toFixed(1)}] The collective consciousness ${worldInfluence === 'hopeful' ? 'brightens' : worldInfluence === 'dystopian' ? 'darkens' : 'maintains'} the reality around you.`;
      const choiceOptions = generateKarmaInfluencedChoices(session.currentKarma, worldKarma, genre);
      const choices = choiceOptions.slice(0, 3);
      const imagePrompt = `${genre} scene reflecting ${karmaLevel} karma, ${session.location}, world karma ${worldInfluence}`;
      console.log('[EnhancedGameplay] Generating scene image with prompt:', imagePrompt);
      const imageUrl = await unifiedAI.generateImage(imagePrompt, genre);
      const newStory: StorySegment = {
        id: uuidv4(),
        narrative: narrative + worldNarrative,
        imagePrompt,
        imageUrl: imageUrl || undefined,
        choices,
        companionMessages: []
      };
      setCurrentStory(newStory);
      setPlayerSession(prev => prev ? { ...prev, storyHistory: [...(prev.storyHistory || []), newStory] } : prev);
      updateLocationBasedOnState(session, genre);
      try {
        await safeTTSService.textToSpeech(newStory.narrative, 'high');
      } catch (e) {
        console.warn('[Audio] Fallback TTS failed', e);
      }
    }
  };

  const generateKarmaInfluencedChoices = (personalKarma: number, worldKarma: number, genre: string) => {
    const combinedKarma = (personalKarma + worldKarma) / 2;
    
    const choices = {
      cyberpunk: {
        positive: [
          'Share encrypted data with the resistance to help overthrow corruption',
          'Donate credits to fund community cyberware clinics',
          'Protect a whistleblower from corporate assassins',
          'Hack the power grid to restore electricity to the slums'
        ],
        negative: [
          'Sell citizen data to the highest corporate bidder',
          'Plant malware in the public transit system',
          'Frame an innocent for your crimes',
          'Exploit a vulnerability in the medical network'
        ],
        neutral: [
          'Gather more intelligence before taking action',
          'Negotiate a deal between rival factions',
          'Explore the black market for information',
          'Maintain your cover while observing'
        ]
      },
      solarpunk: {
        positive: [
          'Optimize the solar grid for maximum community benefit',
          'Share your vertical farming techniques freely',
          'Organize a neighborhood sustainability workshop',
          'Connect isolated communities to the green network'
        ],
        negative: [
          'Sabotage the renewable energy systems',
          'Hoard resources despite community need',
          'Introduce invasive species to the ecosystem',
          'Corrupt the water purification algorithms'
        ],
        neutral: [
          'Study the ecological balance before intervening',
          'Document the sustainability practices',
          'Trade fairly at the community market',
          'Maintain the existing systems'
        ]
      },
      grimdark: {
        positive: [
          'Share your last rations with starving children',
          'Risk your life to save others from the void',
          'Light a candle of hope in the darkness',
          'Preserve fragments of the old world\'s beauty'
        ],
        negative: [
          'Sacrifice others to ensure your survival',
          'Spread the corruption for personal power',
          'Destroy the last remnants of hope',
          'Embrace the void and become its herald'
        ],
        neutral: [
          'Survive another day without compromising',
          'Search for resources in the wasteland',
          'Keep moving to avoid the darkness',
          'Document the world\'s descent'
        ]
      }
    };
    
    const karmaCategory = combinedKarma > 10 ? 'positive' : 
                         combinedKarma < -10 ? 'negative' : 'neutral';
    
    const genreChoices = choices[genre] || choices.cyberpunk;
    const categoryChoices = genreChoices[karmaCategory];
    
    // Mix in some choices from other categories for variety
    const mixedChoices = [...categoryChoices];
    if (Math.random() < 0.3) {
      const otherCategories = Object.keys(genreChoices).filter(k => k !== karmaCategory);
      const randomCategory = otherCategories[Math.floor(Math.random() * otherCategories.length)];
      mixedChoices.push(...genreChoices[randomCategory].slice(0, 1));
    }
    
    return mixedChoices.sort(() => 0.5 - Math.random());
  };

  const updateLocationBasedOnState = (session: PlayerSession, genre: string) => {
    if (Math.random() < 0.3) {
      const locations = {
        cyberpunk: [
          'Neo-Tokyo Central Plaza',
          'Corporate District Servers',
          'Underground Hacker Den',
          'Digital Market Exchange',
          'Quantum Processing Center',
          'Virtual Reality Arcade'
        ],
        solarpunk: [
          'Community Solar Garden',
          'Vertical Farm Complex',
          'Renewable Energy Hub',
          'Ecological Preserve',
          'Sustainable Living Pods',
          'Green Tech Innovation Lab'
        ],
        grimdark: [
          'Abandoned Sector Seven',
          'The Consuming Void',
          'Corrupted Data Wastes',
          'Shadow Market',
          'The Last Sanctuary',
          'Ruins of Hope District'
        ]
      };
      
      const genreLocations = locations[genre] || locations.cyberpunk;
      const newLocation = genreLocations[Math.floor(Math.random() * genreLocations.length)];
      setPlayerSession(prev => prev ? { ...prev, location: newLocation } : null);
    }
  };

  const handleTeleport = async (nodeId: string, targetTime: string) => {
    if (!playerSession || !currentBranchId) return;
    
    try {
      // Find the target commit in our branch history
      const branch = await gitGameStateService.getBranch(currentBranchId);
      const targetCommit = branch.playerChoices.find(choice => choice.hash === nodeId);
      
      if (!targetCommit) {
        throw new Error('Target commit not found');
      }
      
      // Apply stress for temporal displacement
      if (yearZeroCharacter) {
        const stressAmount = Math.abs(new Date(targetTime).getTime() - Date.now()) / (1000 * 60 * 60 * 24); // Days difference
        const updatedCharacter = yearZeroEngine.applyStress(yearZeroCharacter, Math.floor(stressAmount), 'Temporal displacement');
        setYearZeroCharacter(updatedCharacter);
        setCurrentStress(updatedCharacter.stress);
      }
      
      // Update player session to reflect teleport
      const updatedSession = {
        ...playerSession,
        currentKarma: (targetCommit.choiceData?.karmaImpact || targetCommit.karmaDelta || 0),
        gameState: {
          ...playerSession.gameState,
          teleportedFrom: new Date().toISOString(),
          teleportedTo: targetTime
        }
      };
      setPlayerSession(updatedSession);
      
      // Create teleport message
      const teleportMessage: ChatMessage = {
        id: uuidv4(),
        sender: 'Temporal System',
        text: `⚡ Temporal teleport successful! You've jumped to: ${new Date(targetTime).toLocaleString()}. Reality feels... different.`,
        timestamp: Date.now()
      };
      setChatMessages(prev => [...prev, teleportMessage]);
      
      // Generate new story based on teleport destination
      const teleportImagePrompt = `temporal teleport arrival, cyberpunk setting, reality distortion effects, karma energy of ${targetCommit.choiceData?.karmaImpact || 0}, time displacement visual effects`;
      
      // Generate teleport scene image
      console.log('[EnhancedGameplay] Generating teleport scene image with prompt:', teleportImagePrompt);
      const teleportImageUrl = await unifiedAI.generateImage(teleportImagePrompt, 'cyberpunk');

      const teleportStory: StorySegment = {
        narrative: `Reality shimmers as you materialize at your chosen temporal coordinates. You are now at the moment when "${targetCommit.choiceData?.choiceText || 'unknown choice'}" was made. The karmic resonance of this decision (${targetCommit.choiceData?.karmaImpact || 0}) still echoes through the digital fabric. You can feel the weight of causality pressing against your consciousness. What will you do differently this time?`,
        imagePrompt: teleportImagePrompt,
        imageUrl: teleportImageUrl || undefined,
        choices: [
          'Observe without interfering to avoid paradoxes',
          'Make the same choice to preserve the timeline',
          'Choose differently to create a new branch',
          'Attempt to merge this timeline with your original path'
        ],
        companionMessages: [
          { name: 'Temporal', dialogue: `Timeline displacement detected. Causal stress: ${Math.floor(stressAmount)}` },
          { name: 'Karma', dialogue: `Target karma resonance: ${targetCommit.choiceData?.karmaImpact || 0}` }
        ]
      };
      
      setCurrentStory(teleportStory);
      
    } catch (error) {
      console.error('[Gameplay] Teleport failed:', error);
      
      const errorMessage: ChatMessage = {
        id: uuidv4(),
        sender: 'Temporal System',
        text: `❌ Temporal teleport failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        timestamp: Date.now()
      };
      setChatMessages(prev => [...prev, errorMessage]);
    }
  };

  const getKarmaColor = (karma: number) => {
    if (karma > 20) return 'text-green-400';
    if (karma > 0) return 'text-cyan-400';
    if (karma > -20) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getKarmaLabel = (karma: number) => {
    if (karma > 50) return 'Digital Saint';
    if (karma > 20) return 'Cyber Hero';
    if (karma > 0) return 'Net Runner';
    if (karma > -20) return 'Grey Hat';
    if (karma > -50) return 'Data Thief';
    return 'Void Walker';
  };

  if (isLoading) {
    return <LoadingIndicator />;
  }

  // If graphical mode, render GraphicalMode component instead
  if (displayMode === 'graphical') {
    return (
      <ErrorBoundary
        fallback={
          <div className="min-h-screen bg-black text-white p-8 flex flex-col items-center justify-center">
            <h1 className="text-2xl font-bold text-red-400 mb-4">Graphical Mode Error</h1>
            <p className="text-gray-400 mb-6">The graphical renderer encountered an error. You can return to text mode.</p>
            <button
              onClick={() => setDisplayMode('text')}
              className="px-6 py-2 bg-cyan-600 hover:bg-cyan-700 rounded-lg transition-colors"
            >
              ← Return to Text Mode
            </button>
          </div>
        }
      >
        <GraphicalMode onExit={() => setDisplayMode('text')} />
      </ErrorBoundary>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 text-white p-4">
      {/* Header with Karma Display */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="bg-black/50 backdrop-blur-md rounded-lg p-4 border border-purple-500/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                {playerSession?.characterName || 'Loading...'}
              </h1>
              <div className="flex items-center space-x-2">
                <span className="text-gray-400">Personal Karma:</span>
              <span className={`font-bold ${getKarmaColor(playerSession?.currentKarma || 0)}`}>
                  {(playerSession?.currentKarma || 0).toFixed(1)} ({getKarmaLabel(playerSession?.currentKarma || 0)})
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-gray-400">World Karma:</span>
              <span className={`font-bold ${getKarmaColor(worldKarma)}`}>
                {worldKarma.toFixed(1)}
              </span>
            </div>
            {/* Mode Toggle */}
            <button
              onClick={() => setDisplayMode('graphical')}
              className="px-3 py-1 bg-cyan-600/30 hover:bg-cyan-600/50 border border-cyan-400/50 rounded-lg text-sm font-semibold transition-all"
              title="Switch to graphical MMORPG mode"
            >
              🗺️ Map View
            </button>
          </div>
          <div className="flex items-center space-x-4">
            {narrationUrl && (
              <div className="hidden md:block w-64">
                <audio src={narrationUrl} controls className="w-full" />
              </div>
            )}
            <span className="text-sm text-purple-400">
              Genre: {playerSession?.gameState?.genre || 'cyberpunk'}
            </span>
              <span className="text-sm text-cyan-400">
                📍 {playerSession?.location}
              </span>
              <div className="flex space-x-2">
                <button
                  onClick={() => setShowGitTree(!showGitTree)}
                  className="px-3 py-2 bg-purple-600/20 hover:bg-purple-600/30 border border-purple-500 rounded-lg transition-all text-sm"
                >
                  {showGitTree ? 'Hide' : 'Show'} Timeline
                </button>
                <button
                  onClick={() => { try { window.dispatchEvent(new Event('navigate-to-combat')); } catch {} }}
                  className="px-3 py-2 bg-blue-600/20 hover:bg-blue-600/30 border border-blue-500 rounded-lg transition-all text-sm"
                  title="Start PvE Combat"
                >
                  Enter Combat
                </button>
                <button
                  onClick={() => { try { window.dispatchEvent(new Event('open-trading-modal')); } catch {} }}
                  className="px-3 py-2 bg-amber-600/20 hover:bg-amber-600/30 border border-amber-500 rounded-lg transition-all text-sm"
                  title="Open Trading"
                >
                  Trading
                </button>
                <button
                  onClick={() => { try { window.dispatchEvent(new Event('open-character-chat')); } catch {} }}
                  className="px-3 py-2 bg-green-600/20 hover:bg-green-600/30 border border-green-500 rounded-lg transition-all text-sm"
                  title="Character Chat - Communicate through your persona"
                >
                  💬 Chat
                </button>
                <button
                  onClick={onExit}
                  className="px-4 py-2 bg-red-600/20 hover:bg-red-600/30 border border-red-500 rounded-lg transition-all"
                >
                  Exit Game
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Git Tree Visualization */}
      {showGitTree && currentBranchId && (
        <div className="max-w-7xl mx-auto mb-6">
          <InteractiveGitTreeVisualizer
            cartridgeId={validCartridgeId || safeCartridgeId}
            userId={userProfile?.uid || 'demo-user'}
            currentBranchId={currentBranchId}
            playerKarma={playerSession?.currentKarma || 0}
            onTeleport={handleTeleport}
            onMergeSelect={handleMerge}
            isVisible={showGitTree}
          />
        </div>
      )}

      {/* Main Game Content */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Story Display */}
        <div className="lg:col-span-2 space-y-4">
          {currentStory && (
            <div className="bg-black/50 backdrop-blur-md rounded-lg p-6 border border-purple-500/30">
              {/* Scene Image with Loading State */}
              <div className="mb-6 rounded-lg overflow-hidden relative bg-gradient-to-br from-gray-900 via-purple-900/20 to-gray-900">
                <CyberpunkImageLoader
                  src={currentStory.imageUrl}
                  alt={`Scene: ${currentStory.narrative.substring(0, 50)}...`}
                  className="w-full h-48 md:h-64 object-cover"
                  loadingText="Materializing cybernetic vision..."
                  enableGlitch={true}
                />
              </div>
              
              <div className="prose prose-invert max-w-none">
                <p className="text-lg leading-relaxed">{currentStory.narrative}</p>
              </div>
              
              {/* Companion Messages */}
              {currentStory.companionMessages && currentStory.companionMessages.length > 0 && (
                <div className="mt-4 space-y-2">
                  {currentStory.companionMessages.map((msg, idx) => (
                    <div key={idx} className="flex items-start space-x-2">
                      <span className="font-bold text-purple-400">{msg.name}:</span>
                      <span className="text-gray-300">{msg.dialogue}</span>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Choices */}
              <div className="mt-6 space-y-3">
                {(currentStory.choices || []).map((choice, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleChoice(choice)}
                    disabled={isGeneratingResponse}
                    className={`w-full text-left p-4 rounded-lg transition-all ${
                      selectedChoice === choice
                        ? 'bg-purple-600/30 border-purple-400'
                        : 'bg-gray-800/50 hover:bg-gray-700/50 border-gray-600'
                    } border ${isGeneratingResponse ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <span className="text-cyan-400 mr-2">{idx + 1}.</span>
                    {choice}
                  </button>
                ))}

                {/* Free-Form Decision Input */}
                <div className="mt-4 pt-4 border-t border-purple-500/30">
                  <h4 className="text-sm font-semibold text-purple-300 mb-2">💭 Or forge your own path:</h4>
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={customChoice}
                      onChange={(e) => setCustomChoice(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && customChoice.trim()) {
                          handleChoice(customChoice.trim());
                          setCustomChoice('');
                        }
                      }}
                      placeholder="Type your own action (e.g., 'Hack the mainframe', 'Negotiate peace')"
                      disabled={isGeneratingResponse}
                      className="flex-1 px-4 py-3 bg-gray-900/70 border border-purple-500/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-400 focus:bg-gray-900/90 disabled:opacity-50 transition-all"
                    />
                    <button
                      onClick={() => {
                        if (customChoice.trim()) {
                          handleChoice(customChoice.trim());
                          setCustomChoice('');
                        }
                      }}
                      disabled={isGeneratingResponse || !customChoice.trim()}
                      className="px-6 py-3 bg-gradient-to-r from-purple-600/30 to-pink-600/30 hover:from-purple-600/50 hover:to-pink-600/50 border border-purple-400 rounded-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      ⚡ Execute
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Your custom actions influence the narrative and karma system uniquely
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Merge Opportunities */}
          {mergeOpportunities.length > 0 && (
            <div className="bg-gradient-to-r from-purple-900/50 to-pink-900/50 backdrop-blur-md rounded-lg p-4 border border-purple-500/30">
              <h3 className="text-lg font-bold text-purple-300 mb-2">🔀 Merge Opportunities</h3>
              <p className="text-sm text-gray-300 mb-3">
                Other players' stories are converging with yours! Merge to create shared narratives.
              </p>
              <div className="space-y-2">
                {mergeOpportunities.slice(0, 3).map((opp, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleMerge(opp.branchId)}
                    disabled={!uuidValidate(opp.branchId)}
                    className="w-full text-left p-3 bg-black/30 hover:bg-black/50 rounded-lg border border-purple-500/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="flex justify-between items-center">
                      <span className="text-cyan-400">{opp.branchData.name}</span>
                      <span className="text-sm text-gray-400">
                        Karma Diff: {opp.karmaDifference.toFixed(1)} | Shared: {opp.sharedCommits}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Side Panel */}
        <div className="space-y-4">
          {/* Dynamic Companions */}
          <div className="bg-black/50 backdrop-blur-md rounded-lg p-4 border border-purple-500/30">
            <h3 className="text-lg font-bold text-purple-300 mb-2">🎭 Companions in {currentLocation}</h3>
            <p className="text-xs text-gray-500 mb-3">Characters come and go based on your journey</p>

            {dynamicCompanions.length > 0 ? (
              <div className="space-y-2">
                {dynamicCompanions.map((companion) => (
                  <button
                    key={companion.id}
                    onClick={() => setSelectedCompanion(companion)}
                    className={`w-full text-left p-3 rounded-lg transition-all ${
                      selectedCompanion?.id === companion.id
                        ? 'bg-purple-600/30 border-purple-400'
                        : 'bg-gray-800/50 hover:bg-gray-700/50 border-gray-600'
                    } border`}
                  >
                    <div className="font-semibold text-cyan-400">{companion.name}</div>
                    <div className="text-xs text-gray-400">{companion.archetype.replace('_', ' ')}</div>
                    <div className="text-xs text-purple-400">
                      {companion.personality.slice(0, 2).join(', ')}
                    </div>
                    {companion.duration === 'temporary' && (
                      <div className="text-xs text-orange-400 mt-1">⏱️ Temporary</div>
                    )}
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-gray-400 text-sm">No one here... yet</p>
            )}
          </div>

          {/* Active Mutations Panel */}
          {playerMutations.length > 0 && (
            <div className="bg-black/50 backdrop-blur-md rounded-lg p-4 border border-green-500/30">
              <h3 className="text-lg font-bold text-green-300 mb-3">🧬 Active Mutations</h3>
              <div className="space-y-2">
                {playerMutations.map((mut) => (
                  <div
                    key={mut.id}
                    className="p-3 rounded-lg bg-green-900/20 border border-green-500/30"
                  >
                    <div className="font-semibold text-green-400">{mut.name}</div>
                    <div className="text-xs text-gray-400">{mut.type} • Tier {mut.tier}</div>
                    {mut.effects.stat && (
                      <div className="text-xs text-cyan-400 mt-1">
                        +{mut.effects.value} {mut.effects.stat}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Nearby Players */}
          {nearbyPlayers.length > 0 && (
            <div className="bg-black/50 backdrop-blur-md rounded-lg p-4 border border-cyan-500/30">
              <h3 className="text-lg font-bold text-cyan-300 mb-3">👥 Other Dreamers ({nearbyPlayers.length})</h3>
              <div className="space-y-2">
                {nearbyPlayers.map((player) => (
                  <div
                    key={player.playerId}
                    className="p-3 rounded-lg bg-cyan-900/20 border border-cyan-500/30"
                  >
                    <div className="font-semibold text-cyan-300">{player.playerName}</div>
                    <div className="text-xs text-gray-400">{player.currentActivity}</div>
                    <div className="text-xs">
                      Karma: <span className={`${player.karma > 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {player.karma.toFixed(1)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Companion Chat with Realm Influence */}
          <div className="bg-black/50 backdrop-blur-md rounded-lg p-4 border border-purple-500/30">
            <h3 className="text-lg font-bold text-purple-300 mb-3">🤖 Neural Feed</h3>
            <div className="h-48 overflow-y-auto space-y-2 mb-3">
              {chatMessages.map((msg) => (
                <div key={msg.id} className="text-sm flex items-center justify-between gap-2">
                  <div>
                    <span className="font-semibold text-cyan-400">{msg.sender}:</span>
                    <span className="text-gray-300 ml-2">{msg.text}</span>
                  </div>
                  <button
                    onClick={() => playMessageTTS(msg.text)}
                    className="px-2 py-1 text-xs bg-gray-800 hover:bg-gray-700 border border-gray-600 rounded"
                    title="Play voice"
                  >
                    ▶
                  </button>
                </div>
              ))}
            </div>

            {/* Companion Communication Input */}
            {activeAgent && (
              <div className="pt-3 border-t border-purple-500/30">
                <p className="text-xs text-purple-400 mb-2">
                  💬 Talk to {activeAgent.name} (influences realm dynamics)
                </p>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={companionMessage}
                    onChange={(e) => setCompanionMessage(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && companionMessage.trim()) {
                        handleCompanionChat(companionMessage.trim());
                      }
                    }}
                    placeholder={`Ask ${activeAgent.name} for advice...`}
                    disabled={isGeneratingResponse}
                    className="flex-1 px-3 py-2 bg-gray-900/70 border border-purple-500/30 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-purple-400 disabled:opacity-50"
                  />
                  <button
                    onClick={() => {
                      if (companionMessage.trim()) {
                        handleCompanionChat(companionMessage.trim());
                      }
                    }}
                    disabled={isGeneratingResponse || !companionMessage.trim()}
                    className="px-4 py-2 bg-purple-600/30 hover:bg-purple-600/50 border border-purple-400 rounded-lg text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Send
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Music Player */}
          {validCartridgeId && (
            <MusicPlayer cartridge={playerSession ? {
              id: playerSession.cartridgeId,
              name: cartridgeName,
              description: '',
              version: '1.0.0',
              author: '',
              authorId: '',
              thumbnailUrl: undefined,
              tags: [],
              archetypes: [],
              companions: [],
              worldSettings: { setting: playerSession.gameState?.genre || 'cyberpunk', timeperiod: '', themes: [], atmosphere: '', initialLocation: '', imageStyle: '' },
              storyPrompts: { systemPrompt: '', chatSystemPrompt: '', initialNarrative: '', companionIntro: '', initialScene: '', initialImagePrompt: '' },
              isBuiltIn: false,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              isPublic: true,
              maxPlayers: 4,
              supportedFeatures: []
            } as any : null} />
          )}

          {/* Quests */}
          <QuestPanel
            quests={quests}
            onMark={(id, delta) => {
              const uid = userProfile?.uid || 'demo-user';
              const cid = playerSession?.cartridgeId || validCartridgeId || safeCartridgeId;
              const q = questService.markProgress(uid, cid, id, delta || 1);
              if (q) setQuests(questService.getActiveQuests(uid, cid));
            }}
            onComplete={(id) => {
              const uid = userProfile?.uid || 'demo-user';
              const cid = playerSession?.cartridgeId || validCartridgeId || safeCartridgeId;
              const q = questService.complete(uid, cid, id);
              if (q) setQuests(questService.getActiveQuests(uid, cid));
            }}
          />

          {/* Year Zero Stats */}
          {yearZeroCharacter && (
            <div className="bg-black/50 backdrop-blur-md rounded-lg p-4 border border-purple-500/30">
              <h3 className="text-lg font-bold text-purple-300 mb-3">Year Zero Stats</h3>
              <div className="space-y-3 text-sm">
                <div>
                  <span className="text-gray-400 block mb-1">Stress Level:</span>
                  <div className="flex items-center space-x-2">
                    <div className="flex-1 bg-gray-700 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full transition-all ${currentStress > 10 ? 'bg-red-500' : currentStress > 5 ? 'bg-yellow-500' : 'bg-green-500'}`}
                        style={{ width: `${Math.min((currentStress / 15) * 100, 100)}%` }}
                      />
                    </div>
                    <span className={`text-sm ${currentStress > 10 ? 'text-red-400' : currentStress > 5 ? 'text-yellow-400' : 'text-green-400'}`}>
                      {currentStress}/15
                    </span>
                  </div>
                </div>
                
                <div>
                  <span className="text-gray-400 block mb-1">Active Mutations:</span>
                  {activeMutations.length > 0 ? (
                    <div className="space-y-1">
                      {activeMutations.map((mutation, idx) => (
                        <div key={idx} className="text-xs">
                          <span className="text-cyan-400">{mutation.name}</span>
                          <span className="text-gray-500 ml-2">Lv.{mutation.evolutionLevel}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span className="text-gray-500 text-xs">No active mutations</span>
                  )}
                </div>

                <div>
                  <span className="text-gray-400 block mb-1">Attributes:</span>
                  {yearZeroCharacter.attributes?.map((attr, idx) => (
                    <div key={idx} className="flex justify-between text-xs">
                      <span className="text-gray-300">{attr.name}:</span>
                      <span className="text-cyan-400">{attr.current}/{attr.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Git Branch Info */}
          <div className="bg-black/50 backdrop-blur-md rounded-lg p-4 border border-purple-500/30">
            <h3 className="text-lg font-bold text-purple-300 mb-3">Story Branch</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">Branch ID:</span>
                <span className="text-cyan-400 font-mono">{currentBranchId?.substring(0, 8)}...</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Commits:</span>
                <span className="text-cyan-400">{karmaHistory.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Merge Ready:</span>
                <span className="text-cyan-400">{mergeOpportunities.length} branches</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Timeline:</span>
                <button
                  onClick={() => setShowGitTree(!showGitTree)}
                  className="text-purple-400 hover:text-purple-300 text-xs underline"
                >
                  {showGitTree ? 'Hide' : 'View'} Tree
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Merge Modal */}
      {showMergeModal && mergeOpportunities.length > 0 && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gray-900 rounded-lg p-6 max-w-md w-full border border-purple-500/50">
            <h2 className="text-2xl font-bold text-purple-400 mb-4">Story Convergence Detected!</h2>
            <p className="text-gray-300 mb-4">
              Your narrative path is aligning with another player's story. Would you like to merge your branches?
            </p>
            <div className="space-y-3 mb-4">
              {mergeOpportunities[0] && (
                <div className="p-3 bg-black/50 rounded-lg">
                  <div className="text-cyan-400">{mergeOpportunities[0].branchData.name}</div>
                  <div className="text-sm text-gray-400">
                    Karma Difference: {mergeOpportunities[0].karmaDifference.toFixed(1)}
                  </div>
                  <div className="text-sm text-gray-400">
                    Shared Narrative Points: {mergeOpportunities[0].sharedCommits}
                  </div>
                </div>
              )}
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => handleMerge(mergeOpportunities[0].branchId)}
                disabled={!uuidValidate(mergeOpportunities[0].branchId)}
                className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Merge Stories
              </button>
              <button
                onClick={() => setShowMergeModal(false)}
                className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-all"
              >
                Continue Solo
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mutation Acquisition Modal */}
      {showMutationModal && newMutation && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gradient-to-br from-purple-900/90 to-black border-2 border-purple-400 rounded-lg p-8 max-w-md">
            <h2 className="text-2xl font-bold text-purple-300 mb-4">✨ Mutation Unlocked!</h2>

            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-bold text-cyan-400">{newMutation.name}</h3>
                <p className="text-sm text-purple-400 mb-2">
                  {newMutation.type.charAt(0).toUpperCase() + newMutation.type.slice(1)} • Tier {newMutation.tier}
                </p>
                <p className="text-gray-300">{newMutation.description}</p>
              </div>

              <div className="bg-black/50 p-3 rounded border border-purple-500/30">
                <p className="text-sm text-gray-400 italic">{newMutation.visual}</p>
              </div>

              <div className="text-sm text-cyan-300">
                <strong>Effects:</strong>
                {newMutation.effects.stat && (
                  <span className="ml-2">+{newMutation.effects.value} {newMutation.effects.stat}</span>
                )}
                {newMutation.effects.ability && (
                  <span className="ml-2 text-purple-400">Ability: {newMutation.effects.ability}</span>
                )}
              </div>

              {newMutation.conflicts.length > 0 && (
                <div className="text-xs text-red-400">
                  Conflicts with: {newMutation.conflicts.join(', ')}
                </div>
              )}
            </div>

            <div className="flex gap-4 mt-6">
              <button
                onClick={() => handleMutationAcquisition(newMutation)}
                className="flex-1 px-4 py-3 bg-purple-600 hover:bg-purple-700 rounded-lg font-semibold transition-all"
              >
                Accept Mutation
              </button>
              <button
                onClick={() => {
                  setShowMutationModal(false);
                  setNewMutation(null);
                }}
                className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 rounded-lg transition-all"
              >
                Decline
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedGameplayScreen;
